
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  Bot, 
  Settings, 
  MessageSquare, 
  BarChart3, 
  Play,
  Pause,
  RefreshCcw,
  RotateCcw
} from 'lucide-react';

const HQAgents = () => {
  const agents = [
    {
      id: 1,
      name: "Miles",
      role: "Customer Support AI",
      status: "active",
      uptime: "99.8%",
      conversations: 1247,
      interactions: 5893,
      lastUpdated: "2 hours ago",
      description: "Handles patient inquiries and appointment scheduling"
    },
    {
      id: 2,
      name: "Alma",
      role: "Training & Knowledge AI",
      status: "active",
      uptime: "99.9%",
      conversations: 832,
      interactions: 4215,
      lastUpdated: "1 day ago",
      description: "Manages staff training modules and knowledge base"
    },
    {
      id: 3,
      name: "Devon",
      role: "Clinical Decision Support",
      status: "maintenance",
      uptime: "97.2%",
      conversations: 456,
      interactions: 2109,
      lastUpdated: "3 days ago",
      description: "Assists providers with treatment recommendations"
    }
  ];

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'bg-emerald-500/20 text-emerald-200 border-emerald-500/30';
      case 'training': return 'bg-blue-500/20 text-blue-200 border-blue-500/30';
      case 'maintenance': return 'bg-amber-500/20 text-amber-200 border-amber-500/30';
      case 'offline': return 'bg-red-500/20 text-red-200 border-red-500/30';
      default: return 'bg-slate-500/20 text-slate-200 border-slate-500/30';
    }
  };

  const getActionButton = (status: string) => {
    if (status === 'active') {
      return (
        <Button size="sm" variant="outline" className="border-slate-600 text-slate-300">
          <Pause className="w-4 h-4 mr-2" />
          Pause
        </Button>
      );
    } else if (status === 'maintenance') {
      return (
        <Button size="sm" className="bg-amber-600 hover:bg-amber-700">
          <RefreshCcw className="w-4 h-4 mr-2" />
          Restart
        </Button>
      );
    } else {
      return (
        <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700">
          <Play className="w-4 h-4 mr-2" />
          Activate
        </Button>
      );
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-amber-900">
      {/* Header */}
      <div className="bg-slate-800/50 backdrop-blur border-b border-amber-500/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div>
              <h1 className="text-3xl font-bold text-white">
                AI Agent Control
              </h1>
              <p className="mt-1 text-amber-200">
                Manage Miles, Alma, Devon, and other AI assistants
              </p>
            </div>
            <Button className="bg-amber-600 hover:bg-amber-700 text-white">
              <Bot className="w-4 h-4 mr-2" />
              Deploy New Agent
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="space-y-8">
          {/* System Overview */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Card className="bg-slate-800/50 backdrop-blur border-emerald-500/20">
              <CardContent className="p-6">
                <div className="flex items-center">
                  <div className="w-12 h-12 rounded-full flex items-center justify-center mr-4 bg-emerald-500/20">
                    <Bot className="h-6 w-6 text-emerald-400" />
                  </div>
                  <div>
                    <p className="text-sm text-emerald-200">Active Agents</p>
                    <p className="text-2xl font-bold text-white">2</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-slate-800/50 backdrop-blur border-blue-500/20">
              <CardContent className="p-6">
                <div className="flex items-center">
                  <div className="w-12 h-12 rounded-full flex items-center justify-center mr-4 bg-blue-500/20">
                    <MessageSquare className="h-6 w-6 text-blue-400" />
                  </div>
                  <div>
                    <p className="text-sm text-blue-200">Daily Conversations</p>
                    <p className="text-2xl font-bold text-white">187</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-slate-800/50 backdrop-blur border-purple-500/20">
              <CardContent className="p-6">
                <div className="flex items-center">
                  <div className="w-12 h-12 rounded-full flex items-center justify-center mr-4 bg-purple-500/20">
                    <Settings className="h-6 w-6 text-purple-400" />
                  </div>
                  <div>
                    <p className="text-sm text-purple-200">System Health</p>
                    <p className="text-2xl font-bold text-white">98.7%</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* AI Agents Grid */}
          <Card className="bg-slate-800/50 backdrop-blur border-slate-600/30">
            <CardHeader>
              <CardTitle className="text-white">Active AI Agents</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                {agents.map((agent) => (
                  <div key={agent.id} className="p-4 bg-slate-700/30 rounded-lg border border-slate-600/30">
                    <div className="grid grid-cols-1 lg:grid-cols-5 gap-4 items-center">
                      <div className="col-span-2">
                        <div className="flex items-start space-x-3">
                          <div className="w-12 h-12 bg-gradient-to-r from-violet-500 to-purple-600 rounded-full flex items-center justify-center text-white font-bold text-xl">
                            {agent.name[0]}
                          </div>
                          <div>
                            <h3 className="text-white font-semibold flex items-center">
                              {agent.name}
                              <Badge className={`ml-2 ${getStatusColor(agent.status)}`}>
                                {agent.status}
                              </Badge>
                            </h3>
                            <p className="text-slate-300 text-sm">{agent.role}</p>
                            <p className="text-slate-400 text-xs mt-1">{agent.description}</p>
                          </div>
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <p className="text-slate-400 text-xs">Conversations</p>
                          <p className="text-white">{agent.conversations}</p>
                        </div>
                        <div>
                          <p className="text-slate-400 text-xs">Interactions</p>
                          <p className="text-white">{agent.interactions}</p>
                        </div>
                      </div>
                      
                      <div>
                        <p className="text-slate-400 text-xs">Uptime</p>
                        <p className="text-emerald-400">{agent.uptime}</p>
                        <p className="text-slate-400 text-xs mt-1">Updated {agent.lastUpdated}</p>
                      </div>
                      
                      <div className="flex space-x-2 justify-end">
                        <Button size="sm" variant="outline" className="border-slate-600 text-slate-300">
                          <Settings className="w-4 h-4 mr-2" />
                          Configure
                        </Button>
                        {getActionButton(agent.status)}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Agent Analytics */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <Card className="bg-slate-800/50 backdrop-blur border-slate-600/30">
              <CardHeader>
                <CardTitle className="text-white flex items-center">
                  <BarChart3 className="h-5 w-5 mr-2 text-blue-400" />
                  Agent Performance
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="p-3 bg-slate-700/30 rounded-lg border border-slate-600/30">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-white font-medium">Miles - Support Ratings</p>
                      <p className="text-emerald-400">4.8/5.0</p>
                    </div>
                    <div className="h-2 w-full bg-slate-600 rounded-full overflow-hidden">
                      <div className="h-full bg-emerald-500" style={{ width: '92%' }}></div>
                    </div>
                  </div>
                  
                  <div className="p-3 bg-slate-700/30 rounded-lg border border-slate-600/30">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-white font-medium">Alma - Training Completion</p>
                      <p className="text-emerald-400">96%</p>
                    </div>
                    <div className="h-2 w-full bg-slate-600 rounded-full overflow-hidden">
                      <div className="h-full bg-emerald-500" style={{ width: '96%' }}></div>
                    </div>
                  </div>
                  
                  <div className="p-3 bg-slate-700/30 rounded-lg border border-slate-600/30">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-white font-medium">Devon - Decision Accuracy</p>
                      <p className="text-amber-400">89%</p>
                    </div>
                    <div className="h-2 w-full bg-slate-600 rounded-full overflow-hidden">
                      <div className="h-full bg-amber-500" style={{ width: '89%' }}></div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-slate-800/50 backdrop-blur border-slate-600/30">
              <CardHeader>
                <CardTitle className="text-white">Quick Actions</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-4">
                  <Button className="bg-slate-700 hover:bg-slate-600 text-white h-auto py-3 flex-col">
                    <RotateCcw className="h-5 w-5 mb-2" />
                    <span className="text-sm">Update All Models</span>
                  </Button>
                  <Button className="bg-slate-700 hover:bg-slate-600 text-white h-auto py-3 flex-col">
                    <MessageSquare className="h-5 w-5 mb-2" />
                    <span className="text-sm">Training Interface</span>
                  </Button>
                  <Button className="bg-slate-700 hover:bg-slate-600 text-white h-auto py-3 flex-col">
                    <BarChart3 className="h-5 w-5 mb-2" />
                    <span className="text-sm">View Analytics</span>
                  </Button>
                  <Button className="bg-slate-700 hover:bg-slate-600 text-white h-auto py-3 flex-col">
                    <Settings className="h-5 w-5 mb-2" />
                    <span className="text-sm">System Settings</span>
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HQAgents;
